import React from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../contexts/ThemeContext";
import { Theme } from "../../theme";

// アニメーション
const scaleIn = keyframes`
	from {
		opacity: 0;
		transform: scale(0.95);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
`;

// Styled Components - 参照される順に定義
const ProjectHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 0.75rem;
`;

const ProjectLogo = styled.div<{ theme: Theme }>`
	display: grid;
	place-items: center;
	width: 48px;
	height: 48px;
	flex-shrink: 0;
	color: ${props => props.theme.colors.primary.main};
	background: ${props => props.theme.colors.background.tertiary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	overflow: hidden;
	border-radius: 0.75rem;
	font-family: ${props => props.theme.typography.fontFamily.mono};
	font-size: 0.75rem;
	font-weight: 700;
	letter-spacing: -0.03em;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: 0.75rem;
	}
`;

const Title = styled.h3<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.primary};
	font-size: ${props => props.theme.typography.roles.title.fontSize};
	font-weight: ${props => props.theme.typography.roles.title.fontWeight};
	margin: 0;
	line-height: ${props => props.theme.typography.roles.title.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.title.letterSpacing};
	white-space: pre-line;
	transition: all 0.3s ease;
	flex: 1;

	@media (max-width: 768px) {
		font-size: 1.1rem;
	}
`;

const Terms = styled.div<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.tertiary};
	font-size: ${props => props.theme.typography.roles.caption.fontSize};
	margin-bottom: 0.75rem;
	font-weight: 500;
`;

const Description = styled.p<{ theme: Theme; compact?: boolean }>`
	color: ${props => props.theme.colors.text.secondary};
	font-size: ${props => props.theme.typography.roles.bodySmall.fontSize};
	line-height: ${props => props.theme.typography.roles.bodySmall.lineHeight};
	margin: 0 0 auto 0;
	flex-grow: 1;
	white-space: pre-line;
	${props => props.compact && `
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 3;
		overflow: hidden;
	`}

	@media (max-width: 768px) {
		font-size: 0.9rem;
	}
`;

const ProjectLinkIcon = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.5rem;
	height: 1.5rem;
	transition: transform 0.3s ease;
`;

const ProjectLink = styled.div<{ theme: Theme }>`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	margin-top: 0.75rem;
	color: ${props => props.theme.colors.primary.main};
	font-weight: ${props => props.theme.typography.roles.label.fontWeight};
	font-size: ${props => props.theme.typography.roles.label.fontSize};
	padding: 0.5rem 0;
	transition: all 0.2s ease;
`;

const ProjectContainer = styled.div<{ theme: Theme; compact?: boolean }>`
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: ${props => props.theme.density.cardRadius};
	padding: ${props => props.compact ? '1rem' : props.theme.density.cardPadding};
	height: 100%;
	display: flex;
	flex-direction: column;
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	position: relative;
	overflow: hidden;
	box-shadow: ${props => props.theme.shadows.base};

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: ${props => props.theme.mode === 'light'
			? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, transparent 100%)'
			: 'linear-gradient(135deg, rgba(129, 140, 248, 0.1) 0%, transparent 100%)'
		};
		opacity: 0;
		transition: opacity 0.2s ease;
	}

	&:hover {
		transform: translateY(-3px);
		box-shadow: ${props => props.theme.shadows.xl};
		border-color: ${props => props.theme.colors.primary.main};

		&::before {
			opacity: 1;
		}

		${Title} {
			color: ${props => props.theme.colors.primary.main};
		}

		${ProjectLink} {
			color: ${props => props.theme.colors.primary.light};
			transform: translateX(4px);
		}

		${ProjectLinkIcon} {
			transform: rotate(-45deg);
		}
	}

	@media (max-width: 768px) {
		padding: ${props => props.theme.density.cardPaddingMobile};
	}
`;

const ProjectCard = styled.div`
	height: 100%;
	animation: ${scaleIn} 0.5s ease-out;

	a {
		text-decoration: none;
		display: block;
		height: 100%;
	}
`;

interface ProjectProps {
	logo: string | null;
	title: string;
	terms?: string;
	description: string;
	linkText: string;
	link: string;
	compact?: boolean;
}

const Project: React.FC<ProjectProps> = (props) => {
	const { logo, title, terms, description, linkText, link, compact } = props;
	const { theme } = useTheme();
	const monogram = title.replace(/[^A-Za-z0-9\p{L}]/gu, "").slice(0, 2).toUpperCase();
	const content = (
		<ProjectContainer theme={theme} compact={compact}>
			<ProjectHeader>
				<ProjectLogo theme={theme}>
					{logo ? (
						<img src={logo} alt={`${title} logo`} />
					) : (
						<span aria-hidden="true">{monogram}</span>
					)}
				</ProjectLogo>
				<Title theme={theme}>{title}</Title>
			</ProjectHeader>
			{terms && <Terms theme={theme}>{terms}</Terms>}
			<Description theme={theme} compact={compact}>{description}</Description>
			{link && (
				<ProjectLink theme={theme}>
					<ProjectLinkIcon>
						<FontAwesomeIcon icon={faLink} />
					</ProjectLinkIcon>
					<span>{linkText}</span>
				</ProjectLink>
			)}
		</ProjectContainer>
	);

	return (
		<ProjectCard>
			{link ? (
				<a href={link} target="_blank" rel="noreferrer">{content}</a>
			) : content}
		</ProjectCard>
	);
};

export default Project;
